/** Pure helpers for billing UI formatting (unit-tested).

Billing buckets and charge schedules are UTC; format datetimes in UTC so the
browser timezone cannot shift the labeled day or the "Next weekly charge" card.
*/

export function formatCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const rem = abs % 100;
  return `${sign}$${dollars.toLocaleString("en-US")}.${String(rem).padStart(2, "0")}`;
}

export function formatMonthKey(key: string): string {
  // key = YYYY-MM
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatDayKey(key: string): string {
  // key = YYYY-MM-DD
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return key;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function formatUtcDateTime(iso: string): string {
  const formatted = new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
  return `${formatted} UTC`;
}

export function formatUtcDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatUtcTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

export function kindLabel(kind: string): string {
  if (kind === "statement_processed") return "Bank statements";
  if (kind === "baselayer_run") return "Background searches";
  if (kind === "baselayer_linked_pull") return "Linked business searches";
  if (kind === "baselayer_web_presence") return "Web & social searches";
  return kind;
}

export function invoiceStatusLabel(status: string): string {
  switch (status) {
    case "open":
      return "Open";
    case "paid":
      return "Paid";
    case "payment_failed":
      return "Payment failed";
    case "void":
      return "Void";
    default:
      return status;
  }
}

export function thresholdProgress(accrued: number, threshold: number): number {
  if (threshold <= 0) return 0;
  return Math.min(100, Math.round((accrued / threshold) * 100));
}
